import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  PUBLIC_USER_STATUS,
  PUBLIC_USER_STATUS_TO_TEXT,
} from "common/constants";
import { GetPaginatedPublicUsersForAccountQuery } from "../../../generated/graphql";
import {
  LegacyTableStateFromURL,
  ManualPaginationConfig,
} from "../../Common/Tables/hooks";
import Table from "../../Common/Tables/Table";
import { StyleVariant, Tag } from "../../Common/Tag";

const PUBLIC_USER_STATUS_TO_TAG_STYLE_VARIANT: Record<
  PUBLIC_USER_STATUS,
  StyleVariant
> = {
  [PUBLIC_USER_STATUS.ACTIVE]: "default",
  [PUBLIC_USER_STATUS.SUSPENDED]: "neutral",
  [PUBLIC_USER_STATUS.UNCONFIRMED]: "warning",
} as const;

const LOADING_TEXT = "Loading public website guests";
const NO_DATA_TEXT = "No public website guests found";

export type PublicUser = NonNullable<
  GetPaginatedPublicUsersForAccountQuery["paginatedPublicUsersForAccount"]["data"][number]
>;

export interface PublicUsersTableProps {
  currentData: Array<PublicUser>;
  loading: boolean;
  manualPaginationConfig: ManualPaginationConfig;
  initialTableState: LegacyTableStateFromURL;
  setTableStateInURL: (state: any) => void;
}

export default ({
  currentData,
  loading,
  manualPaginationConfig,
  initialTableState,
  setTableStateInURL,
}: PublicUsersTableProps) => {
  const Name = (row: PublicUser) => {
    return `${row.firstName} ${row.lastName}`;
  };

  const Status = (row: PublicUser) => {
    return (
      <Tag styleVariant={PUBLIC_USER_STATUS_TO_TAG_STYLE_VARIANT[row.status]}>
        {PUBLIC_USER_STATUS_TO_TEXT[row.status]}
      </Tag>
    );
  };

  let columns = useMemo<Array<ColumnDef<PublicUser>>>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: Name,
        enableSorting: false,
      },
      {
        id: "email",
        header: "Email",
        accessorKey: "email",
        enableSorting: false,
      },
      {
        id: "status",
        header: "Status",
        enableSorting: false,
        cell: ({ row }) => Status(row.original),
      },
    ],
    [],
  );

  return (
    <Table
      currentData={currentData}
      columns={columns}
      tableStyleDetails={{ hasHighlights: true, hasRowActions: true }}
      loadingDetails={{
        loading,
        loadingText: LOADING_TEXT,
        noDataText: NO_DATA_TEXT,
      }}
      initialState={initialTableState}
      manualPaginationConfig={manualPaginationConfig}
      setTableStateInURL={setTableStateInURL}
    />
  );
};
